import * as React from 'react'

import clsx from 'clsx'
import { formatDistanceToNowStrict } from 'date-fns'

import {
  Avatar,
  Flex,
  IconButton,
  PencilIcon,
  Text,
  Tooltip,
  TrashIcon,
} from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'

import { EditNoteForm } from './EditNoteForm'
import styles from '../NotesWidget.module.css'
import { NoteContent } from './NoteContent'

import type { JSX } from 'react'

export const Note_Note = graphql(/* GraphQL */ `
  fragment Note_Note on Note @_unmask {
    id
    text
    createdAt
    updatedAt
    author {
      id
      email
      avatar
      name
    }
    allowedActions
  }
`)

const DEFAULT_AVATAR = 'https://s3.amazonaws.com/buffer-ui/Default+Avatar.png'

export interface NoteProps {
  note: FragmentOf<typeof Note_Note>
  isAuthor: boolean
  onEdit: (noteId: string, text: string) => Promise<void>
  onDelete: (noteId: string) => Promise<void>
}

export const Note = React.forwardRef<HTMLLIElement, NoteProps>(
  ({ note, isAuthor, onEdit, onDelete }: NoteProps, noteRef): JSX.Element => {
    const [isOverflown, setIsOverflown] = React.useState(false)
    const [isEditing, setIsEditing] = React.useState(false)
    const authorNameOrEmailRef = React.useRef<HTMLDivElement>(null)

    const { text, createdAt, author, updatedAt } = note

    const avatar = author?.avatar ?? DEFAULT_AVATAR
    // If the author is the current user, show 'You'
    // Otherwise, show the author's name if it exists and fall back to the email if not
    const authorNameOrEmail = isAuthor ? 'You' : (author?.name ?? author?.email)

    React.useEffect(() => {
      if (!authorNameOrEmailRef.current) return
      const element = authorNameOrEmailRef.current
      // Check if the element is overflown (aka: has ellipsis)
      setIsOverflown(element.scrollWidth > element.clientWidth)
    }, [])

    const showActions =
      note.allowedActions.includes('updateNote') ||
      note.allowedActions.includes('deleteNote')

    const handleSubmitEdit = async (editedText: string): Promise<void> => {
      if (
        !note.allowedActions.includes('updateNote') ||
        !editedText.trim() ||
        editedText === text
      ) {
        return
      }
      onEdit(note.id, editedText)
      setIsEditing(false)
    }

    const handleDeleteNote = (): void => {
      if (!note.allowedActions.includes('deleteNote')) {
        return
      }
      onDelete(note.id)
    }

    const authorTextElement = (
      <Text
        ref={authorNameOrEmailRef}
        size="md"
        weight="medium"
        className={styles.author}
        translate="no"
      >
        {authorNameOrEmail}
      </Text>
    )

    return (
      <li
        ref={noteRef}
        className={clsx(
          styles.note,
          isAuthor && !isEditing && styles.canEditNote,
        )}
      >
        <Avatar src={avatar} alt="Avatar" size="small" />
        <Flex className={styles.noteContent} direction="column" gap="2xs">
          {isEditing ? (
            <EditNoteForm
              value={text}
              onSubmit={handleSubmitEdit}
              onCancel={(): void => setIsEditing(false)}
            />
          ) : (
            <>
              <Flex className={styles.noteHeader} gap="xs" align="baseline">
                {isOverflown ? (
                  <Tooltip
                    content={<span translate="no">{authorNameOrEmail}</span>}
                  >
                    {authorTextElement}
                  </Tooltip>
                ) : (
                  authorTextElement
                )}
                <Text
                  className={styles.createdAt}
                  as="time"
                  weight="medium"
                  color="subtle"
                  dateTime={createdAt}
                >
                  {formatDistanceToNowStrict(createdAt, {
                    addSuffix: true,
                  })}
                </Text>
              </Flex>
              <NoteContent
                text={text}
                isEdited={!!updatedAt && updatedAt !== createdAt}
              />
            </>
          )}
        </Flex>
        {showActions && (
          <Flex gap="xs" className={styles.noteActions}>
            {note.allowedActions.includes('updateNote') && (
              <IconButton
                label="Edit note"
                variant="secondary"
                tooltip="Edit note"
                onClick={(): void => setIsEditing(true)}
              >
                <PencilIcon />
              </IconButton>
            )}
            {note.allowedActions.includes('deleteNote') && (
              <IconButton
                label="Delete note"
                variant="critical"
                tooltip="Delete note"
                onClick={handleDeleteNote}
              >
                <TrashIcon />
              </IconButton>
            )}
          </Flex>
        )}
      </li>
    )
  },
)

Note.displayName = 'Note'
